import React from 'react'
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer'
import _ from 'lodash'
import {
    PieChart,
    Pie,
    Tooltip,
    Legend,
    Cell,
    BarChart,
    Bar,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    AreaChart,
    Area,
} from 'recharts'
import { Plus, Minus } from 'lucide-react'
import GraphBoard from './DonutChart/GraphBoard'
import './ChartConainer.css'
import './DonutChart/Graph.css'



// ---------- AreaChart helpers ----------

const toTime = (value) => {
    if (!value) return NaN

    if (value instanceof Date) return value.getTime()
    if (typeof value === 'number') return value
    if (typeof value !== 'string') return NaN

    const s = value.trim()

    // ISO date: YYYY-MM-DD
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (m) {
        const yyyy = Number(m[1])
        const mm = Number(m[2]) - 1
        const dd = Number(m[3])
        return new Date(yyyy, mm, dd).getTime() // local date, stable for buckets
    }

    // ISO datetime: YYYY-MM-DDTHH:mm:ss...
    m = s.match(/^(\d{4})-(\d{2})-(\d{2})T/)
    if (m) {
        // this one is safe to parse as ISO datetime
        const t = Date.parse(s)
        return Number.isNaN(t) ? NaN : t
    }

    // FR: DD/MM/YYYY (NOT Date.parse)
    m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (m) {
        const dd = Number(m[1])
        const mm = Number(m[2]) - 1
        const yyyy = Number(m[3])
        return new Date(yyyy, mm, dd).getTime()
    }

    // If it doesn't match known safe formats, don't guess.
    return NaN
}


const endOfWeekSunday = (d) => {
    const date = new Date(d.getFullYear(), d.getMonth(), d.getDate())
    const day = date.getDay() // Sun=0..Sat=6
    const add = (7 - day) % 7 // if Sunday => 0, else days until Sunday
    date.setDate(date.getDate() + add)
    date.setHours(0, 0, 0, 0)
    return date
}

const endOfMonth = (d) => {
    // last day of current month: day 0 of next month
    const date = new Date(d.getFullYear(), d.getMonth() + 1, 0)
    date.setHours(0, 0, 0, 0)
    return date
}

const toISODate = (d) => {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
}

const toFRDate = (d) => {
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
}

const aggregateTimeSeriesByPeriodEnd = (rows, xKey, mode, op = "sum", outFormat = "iso") => {
    if (!rows?.length) return rows

    const seriesKeys = Object.keys(rows[0]).filter(k => k !== xKey)
    const buckets = new Map()

    for (const r of rows) {
        const t = toTime(r[xKey])
        if (!t) continue

        const d = new Date(t)

        let bucketDate
        if (mode === "week") bucketDate = endOfWeekSunday(d)
        else if (mode === "month") bucketDate = endOfMonth(d)
        else bucketDate = new Date(d.getFullYear(), d.getMonth(), d.getDate())

        const bucketKey = outFormat === "fr" ? toFRDate(bucketDate) : toISODate(bucketDate)

        let b = buckets.get(bucketKey)
        if (!b) {
            b = { [xKey]: bucketKey, __count: 0 }
            for (const k of seriesKeys) b[k] = 0
            buckets.set(bucketKey, b)
        }

        b.__count += 1
        for (const k of seriesKeys) b[k] += Number(r[k] ?? 0)
    }

    // IMPORTANT: sorting must use toTime() that understands BOTH formats
    let out = Array.from(buckets.values()).sort((a, b) => toTime(a[xKey]) - toTime(b[xKey]))

    if (op === "avg") {
        out = out.map(r => {
            const c = r.__count || 1
            const copy = { ...r }
            for (const k of seriesKeys) copy[k] = copy[k] / c
            delete copy.__count
            return copy
        })
    } else {
        out = out.map(r => {
            const copy = { ...r }
            delete copy.__count
            return copy
        })
    }

    return out
}


const periodMapToSeriesByX = (rawObjects, fmt = {}) => {
    const {
        seriesIdKey = "mesh",
        periodsKey = "periods",
    } = fmt

    const rows = Array.isArray(rawObjects) ? rawObjects : []
    const byDate = new Map()
    const keys = []

    for (const r of rows) {
        const seriesId = String(r?.[seriesIdKey] ?? "")
        if (!seriesId) continue

        keys.push({ key: seriesId, label: seriesId })

        const periods = r?.[periodsKey] || {}
        for (const [date, value] of Object.entries(periods)) {
            let row = byDate.get(date)
            if (!row) {
                row = { date }
                byDate.set(date, row)
            }
            row[seriesId] = Number(value ?? 0)
        }
    }

    // fill missing series values with 0
    const seriesIds = [...new Set(keys.map(k => k.key))]
    const data = [...byDate.values()]
        .sort((a, b) => toTime(a.date) - toTime(b.date))
        .map(row => {
            for (const sid of seriesIds) if (row[sid] == null) row[sid] = 0
            return row
        })

    const dedupKeys = seriesIds.map(sid => ({ key: sid, label: sid }))

    return { data, keys: dedupKeys }
}



const flattenOnce = (arr) =>
    Array.isArray(arr) ? arr.flatMap((x) => (Array.isArray(x) ? x : [x])) : []

/**
 * rawObjects example:
 * [
 *   [{ alertConfig: "1", alertsByDate: [{date, alertNb}, ...] }],
 *   [{ alertConfig: "2", alertsByDate: [{date, alertNb}, ...] }]
 * ]
 *
 * fmt example:
 * {
 *   seriesIdKey: "alertConfig",
 *   seriesDataKey: "alertsByDate",
 *   xKey: "date",
 *   yKey: "alertNb",
 *   seriesLabelPrefixTKey?: "..."
 * }
 */
const mergeSeriesByX = (rawObjects, fmt = {}, t) => {
    const seriesList = flattenOnce(rawObjects).filter(Boolean)

    const {
        seriesIdKey = 'alertConfig',
        seriesDataKey = 'alertsByDate',
        xKey = 'date',
        yKey = 'alertNb',
        seriesLabelPrefixTKey,
        // optional: allow disable later, default true
        fillMissingDates = true,
    } = fmt || {}

    const byX = new Map()
    const seriesKeys = []

    for (const s of seriesList) {
        const seriesId = String(s?.[seriesIdKey] ?? '')
        if (!seriesId) continue

        seriesKeys.push({
            key: seriesId,
            label: seriesLabelPrefixTKey
                ? `${t ? t(seriesLabelPrefixTKey) : 'Series'} ${seriesId}`
                : seriesId,
        })

        const points = Array.isArray(s?.[seriesDataKey]) ? s[seriesDataKey] : []
        for (const p of points) {
            const x = p?.[xKey]
            if (!x) continue

            const row = byX.get(x) || { [xKey]: x }
            row[seriesId] = Number(p?.[yKey] ?? 0)
            byX.set(x, row)
        }
    }

    const uniqueSeriesIds = [...new Set(seriesKeys.map((k) => k.key))]

    // base data (only existing dates)
    let data = [...byX.values()]
        .sort((a, b) => toTime(a[xKey]) - toTime(b[xKey]))
        .map((row) => {
            for (const sid of uniqueSeriesIds) {
                if (row[sid] == null) row[sid] = 0
            }
            return row
        })

    // fill missing DAILY dates between min and max
    if (fillMissingDates && data.length >= 2) {
        // inline ISO parser + formatter (no extra helpers outside this function)
        const parseISO = (s) => {
            const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/)
            if (!m) return null
            const yyyy = Number(m[1])
            const mm = Number(m[2]) - 1
            const dd = Number(m[3])
            return new Date(yyyy, mm, dd) // local midnight
        }

        const formatISO = (d) => {
            const yyyy = d.getFullYear()
            const mm = String(d.getMonth() + 1).padStart(2, '0')
            const dd = String(d.getDate()).padStart(2, '0')
            return `${yyyy}-${mm}-${dd}`
        }

        const MS_DAY = 24 * 60 * 60 * 1000

        const start = parseISO(data[0][xKey])
        const end = parseISO(data[data.length - 1][xKey])

        // only fill if ISO dates are used (safe)
        if (start && end) {
            const map = new Map(data.map((r) => [r[xKey], r]))
            const filled = []

            for (
                let d = new Date(start);
                d.getTime() <= end.getTime();
                d = new Date(d.getTime() + MS_DAY)
            ) {
                const key = formatISO(d)
                const existing = map.get(key)

                if (existing) {
                    // ensure all series exist
                    const copy = { ...existing }
                    for (const sid of uniqueSeriesIds) if (copy[sid] == null) copy[sid] = 0
                    filled.push(copy)
                } else {
                    // create missing date row with zeros
                    const row = { [xKey]: key }
                    for (const sid of uniqueSeriesIds) row[sid] = 0
                    filled.push(row)
                }
            }

            data = filled
        }
    }

    const keys = uniqueSeriesIds.map((sid) => {
        const found = seriesKeys.find((k) => k.key === sid)
        return found || { key: sid, label: sid }
    })

    return { data, keys }
}

// ---------- Shared render helpers ----------

const renderPercentageLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180
    const radius = outerRadius + 20
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
        <text
            x={x}
            y={y}
            fill="#111"
            fontSize={12}
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
        >
            {(percent * 100).toFixed(1)}%
        </text>
    )
}

const renderXAxisTick = (props) => {
    const { x, y, payload, t } = props
    const raw = t ? t(payload.value) : payload.value

    const MAX_LEN = 18
    const label =
        typeof raw === 'string' && raw.length > MAX_LEN ? raw.slice(0, MAX_LEN) + '…' : raw

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end" transform="rotate(-35)" fontSize={10}>
                <title>{raw}</title>
                {label}
            </text>
        </g>
    )
}

const MAX_SIZE = 6

class ChartContainer extends React.Component {
    constructor(props) {
        super(props)
        const allIds = props.allIds || []
        this.state = {
            allIds,
            objects: allIds.map((id) => props.objects[id]),
            axis: props.axis,
            keys: props.keys, // can be undefined for area chart
            filters: props.filters,
            format: props.format,
            aggregate: props.aggregate,
            currentFilter: props.filters && props.filters[0],
            size: 3,
            currentKeyIndex: 0,
        }

        this.setCriteria = this.setCriteria.bind(this)
        this.setSize = this.setSize.bind(this)
        this.setCurrentKeyIndex = this.setCurrentKeyIndex.bind(this)
    }

    setCurrentKeyIndex(index) {
        this.setState({ currentKeyIndex: index })
    }

    setCriteria(value) {
        this.setState({ currentFilter: value })
    }

    getSum() {
        const { objects, filters, currentFilter, keys, currentKeyIndex } = this.state
        const safeKeys = Array.isArray(keys) ? keys : []
        const metricKey = safeKeys[currentKeyIndex]?.key || safeKeys[0]?.key
        if (!metricKey) return 0

        const filteredObjects = filters
            ? objects.filter((o) => o.value === currentFilter.value)
            : objects

        return _.reduce(filteredObjects, (acc, o) => acc + Number(o[metricKey]), 0)
    }

    setSize(key) {
        const size = this.state.size
        this.setState({
            size:
                key === 'plus'
                    ? size === MAX_SIZE
                        ? size
                        : size + 1
                    : size === 0
                        ? 0
                        : size - 1,
        })
    }

    render() {
        const { setFiltersOpened, filtersOpened, type, title, t } = this.props
        const { axis } = this.state

        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <GraphBoard
                    currentFilter={this.state.currentFilter}
                    setFiltersOpened={setFiltersOpened}
                    filtersOpened={filtersOpened}
                    type={type}
                />
                {title ? (
                    type === 'dynamicBarChart' ? (
                        <h4 className="ChartContainerTitle">
                            {t(title)} / {_.get(this.props.formValues, 'indicator.name')}
                        </h4>
                    ) : (
                        <h4 className="ChartContainerTitle">{t(title)}</h4>
                    )
                ) : (
                    ''
                )}

                <div style={{ height: '100%' }}>

                    <AutoSizer>
                        {({ height, width }) => {
                            // ---------- LINE ----------
                            if (type === 'lineChart') {
                                const keys = Array.isArray(this.state.keys) ? this.state.keys : []
                                const COLORS = [
                                    '#4F46E5',
                                    '#22C55E',
                                    '#F97316',
                                    '#0EA5E9',
                                    '#EC4899',
                                    '#EAB308',
                                    '#6366F1',
                                    '#14B8A6',
                                ]

                                return (
                                    <LineChart
                                        width={width}
                                        height={height}
                                        data={this.state.objects}
                                        margin={{ top: 16, right: 24, left: 8, bottom: 45 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey={axis}
                                            interval={0}
                                            tick={(props) => renderXAxisTick({ ...props, t })}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Legend
                                            verticalAlign="top"
                                            formatter={(value) => {
                                                const k = keys.find((k) => k.key === value)
                                                return k ? (t ? t(k.label) : k.label) : value
                                            }}
                                        />
                                        {keys.map((keyObject, index) => (
                                            <Line
                                                key={keyObject.key}
                                                dataKey={keyObject.key}
                                                stroke={COLORS[index % COLORS.length]}
                                                dot={false}
                                                strokeWidth={2}
                                                isAnimationActive={true}
                                            />
                                        ))}
                                    </LineChart>
                                )
                            }

                            // ---------- DONUT ----------
                            if (type === 'donutChart') {
                                const { filters, objects, currentFilter, size, keys, currentKeyIndex } = this.state

                                const formatNumber = (n) =>
                                    new Intl.NumberFormat('fr-FR').format(Number(n ?? 0))

                                const pct = (v) => (total ? ((v / total) * 100) : 0)

                                const safeKeys = Array.isArray(keys) ? keys : []
                                const metricKey = safeKeys[currentKeyIndex]?.key || safeKeys[0]?.key
                                if (!metricKey) return null

                                const filteredObjects = filters
                                    ? objects.filter((o) => o.value === currentFilter.value)
                                    : objects

                                const sortedObjects = _.sortBy(filteredObjects, (o) => Number(o[metricKey])).reverse()

                                const finalObjects =
                                    size + 1 < sortedObjects.length ? sortedObjects.slice(0, size) : sortedObjects

                                if (size + 1 < sortedObjects.length) {
                                    finalObjects.push({
                                        id: 'others',
                                        [axis]: 'others',
                                        [metricKey]: _.reduce(
                                            sortedObjects.slice(size),
                                            (acc, o) => acc + Number(o[metricKey]),
                                            0
                                        ),
                                    })
                                }

                                const donutData = finalObjects.map((o) => ({
                                    name: o[axis],
                                    value: Number(o[metricKey]),
                                }))

                                const total = this.getSum()

                                const COLORS = [
                                    '#4F46E5',
                                    '#22C55E',
                                    '#F97316',
                                    '#0EA5E9',
                                    '#EC4899',
                                    '#EAB308',
                                    '#6366F1',
                                    '#14B8A6',
                                ]
                                const OTHERS_COLOR = '#9CA3AF'

                                const donutSize = Math.min(width * 0.6, height * 0.9)
                                const innerRadius = donutSize * 0.25
                                const outerRadius = donutSize * 0.38

                                return (
                                    <div style={{ width, height }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                flexWrap: 'wrap',
                                                gap: 8,
                                                marginBottom: 12,
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {safeKeys?.length > 1 &&  safeKeys.map((keyObject, index) => (
                                                <button
                                                    key={keyObject.key}
                                                    onClick={() => this.setCurrentKeyIndex(index)}
                                                    style={{
                                                        border: 'none',
                                                        borderRadius: 16,
                                                        padding: '4px 10px',
                                                        fontSize: 12,
                                                        cursor: 'pointer',
                                                        backgroundColor: index === currentKeyIndex ? '#1e74d9' : '#E5E7EB',
                                                        color: index === currentKeyIndex ? '#FFFFFF' : '#111827',
                                                    }}
                                                >
                                                    {t ? t(keyObject.label) : keyObject.label}
                                                </button>
                                            ))}
                                        </div>

                                        <div
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: '100%',
                                                    alignItems: 'center',
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    justifyContent: 'space-around',
                                                }}
                                            >
                                                {/* LEFT LEGEND */}
                                                <div style={{ display: 'flex', flexDirection: 'column', marginRight: 16 }}>
                                                    {donutData.map((entry, index) => {
                                                        const isOthers = entry.name === 'others'
                                                        const color = isOthers ? OTHERS_COLOR : COLORS[index % COLORS.length]

                                                        return (
                                                            <div
                                                                key={entry.name}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    marginBottom: 6
                                                                }}
                                                            >
                                                            <span
                                                                style={{
                                                                    width: 12,
                                                                    height: 12,
                                                                    borderRadius: 2,
                                                                    marginRight: 6,
                                                                    backgroundColor: color,
                                                                }}
                                                            />
                                                                <span
                                                                    style={{
                                                                        fontSize: 14,
                                                                        lineHeight: 1.3,
                                                                        whiteSpace: 'normal',
                                                                    }}
                                                                >
                                                                    {t ? t(entry.name) : entry.name}{' '}
                                                                    <span style={{color: '#6b7280', fontSize: 12}}>
                                                                        {pct(entry.value).toFixed(1)}% ({formatNumber(entry.value)})
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        )
                                                    })}

                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        gap: 8,
                                                        marginTop: 6
                                                    }}>
                                                    <button
                                                            onClick={() => this.setSize('plus')}
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#62626e',
                                                                border: '1px solid #62626e',
                                                                color: '#fff',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease',
                                                            }}
                                                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#111827')}
                                                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#62626e')}
                                                            aria-label="Afficher plus de catégories"
                                                        >
                                                            <Plus size={14} />
                                                        </button>

                                                        <button
                                                            onClick={() => this.setSize('minus')}
                                                            style={{
                                                                width: 32,
                                                                height: 32,
                                                                borderRadius: '50%',
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #d1d5db',
                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.15s ease',
                                                            }}
                                                            onMouseEnter={(e) =>
                                                                (e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)')
                                                            }
                                                            onMouseLeave={(e) =>
                                                                (e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.06)')
                                                            }
                                                            aria-label="Afficher moins de catégories"
                                                        >
                                                            <Minus size={14} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* RIGHT DONUT */}
                                                <PieChart width={donutSize} height={donutSize}>
                                                    <Pie
                                                        data={donutData}
                                                        dataKey="value"
                                                        nameKey="name"
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={innerRadius}
                                                        outerRadius={outerRadius}
                                                        paddingAngle={2}
                                                        isAnimationActive={true}
                                                        animationDuration={800}
                                                        label={renderPercentageLabel}
                                                        labelLine={false}
                                                    >
                                                        {donutData.map((entry, index) => {
                                                            const isOthers = entry.name === 'others'
                                                            const color = isOthers ? OTHERS_COLOR : COLORS[index % COLORS.length]
                                                            return <Cell key={`cell-${index}`} fill={color}/>
                                                        })}
                                                    </Pie>

                                                    <text
                                                        x="50%"
                                                        y="50%"
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        style={{fontSize: 12, fill: '#6b7280'}}
                                                    >
                                                        {t ? t('Total') : 'Total'}
                                                    </text>

                                                    <text
                                                        x="50%"
                                                        y="50%"
                                                        dy={18}
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        style={{fontSize: 18, fontWeight: 700, fill: '#111827'}}
                                                    >
                                                        {formatNumber(total)}
                                                    </text>

                                                    <Tooltip
                                                        formatter={(value, _name, { payload }) => {
                                                            const v = Number(value)
                                                            const percent = total ? ((v / total) * 100).toFixed(1) : '0.0'
                                                            return [formatNumber(v), payload?.name]
                                                        }}
                                                        labelFormatter={(label) => (t ? t(label) : label)}
                                                        contentStyle={{ borderRadius: 8 }}
                                                        itemStyle={{ fontWeight: 600 }}
                                                    />
                                                </PieChart>
                                            </div>
                                        </div>
                                    </div>
                                )
                            }

                            // ---------- BAR / STACKED BAR ----------
                            if (['barChart', 'stackedBarChart'].includes(type)) {
                                const { objects } = this.state
                                const keys = Array.isArray(this.state.keys) ? this.state.keys : []

                                const COLORS = [
                                    '#4F46E5',
                                    '#22C55E',
                                    '#F97316',
                                    '#0EA5E9',
                                    '#EC4899',
                                    '#EAB308',
                                    '#6366F1',
                                    '#14B8A6',
                                ]

                                return (
                                    <BarChart
                                        width={width}
                                        height={height}
                                        data={objects}
                                        margin={{ top: 16, right: 24, left: 8, bottom: 45 }}
                                        isAnimationActive={true}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey={axis}
                                            interval={0}
                                            tick={(props) => renderXAxisTick({ ...props, t })}
                                        />
                                        <YAxis />
                                        <Tooltip
                                            formatter={(value, name) => {
                                                const k = keys.find((k) => k.key === name)
                                                return [value, k ? (t ? t(k.label) : k.label) : name]
                                            }}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            formatter={(value) => {
                                                const k = keys.find((k) => k.key === value)
                                                return k ? (t ? t(k.label) : k.label) : value
                                            }}
                                        />
                                        {keys.map((keyObject, index) => (
                                            <Bar
                                                key={keyObject.key}
                                                dataKey={keyObject.key}
                                                stackId={type === 'stackedBarChart' ? 'a' : undefined}
                                                fill={COLORS[index % COLORS.length]}
                                            />
                                        ))}
                                    </BarChart>
                                )
                            }

                            // ---------- STACKED AREA ----------
                            if (type === 'stackedAreaChart') {
                                // configs are now separated
                                const fmt = this.state.format || null
                                const agg = this.state.aggregate || null


                                // 1) FORMAT step (optional)
                                // If format exists, build {data, keys} from the raw series objects
                                // Otherwise assume `this.state.objects` is already a valid recharts dataset.
                                let data = Array.isArray(this.state.objects) ? this.state.objects : []
                                let keys = Array.isArray(this.state.keys) ? this.state.keys : []

                                if (fmt) {
                                    let out
                                    if (fmt?.kind === 'periodMap') out = periodMapToSeriesByX(this.state.objects, fmt)
                                    else if (fmt?.kind === 'mergeSeriesByX') out = mergeSeriesByX(this.state.objects, fmt)
                                    else out = mergeSeriesByX(this.state.objects, fmt)

                                    data = out.data
                                    keys = out.keys
                                }

                                // x key (prefer fmt.xKey if provided, else axis)
                                const xKey = (fmt && fmt.xKey) ? fmt.xKey : axis

                                // 2) AGGREGATE step (optional)
                                let finalData = data

                                if (agg && data.length >= 2) {
                                    const thresholds = agg.thresholds || { dayMaxDays: 30, weekMaxDays: 90 }

                                    // choose mode
                                    let mode = agg.mode || 'auto'
                                    if (mode === 'auto') {
                                        const MS_DAY = 24 * 60 * 60 * 1000
                                        const days = (toTime(data[data.length - 1][xKey]) - toTime(data[0][xKey])) / MS_DAY
                                        if (days <= thresholds.dayMaxDays) mode = 'day'
                                        else if (days <= thresholds.weekMaxDays) mode = 'week'
                                        else mode = 'month'
                                    }

                                    // apply bucket rule (default matches your existing behavior)
                                    // NOTE: your current aggregateTimeSeriesByPeriodEnd already buckets to endOfWeekSunday/endOfMonth
                                    // so `bucket` is mostly documentation unless you want additional options later.
                                    finalData =
                                        mode === 'day'
                                            ? data
                                            : aggregateTimeSeriesByPeriodEnd(data, xKey, mode, agg.op || 'sum')
                                }

                                const COLORS = [
                                    '#4F46E5',
                                    '#22C55E',
                                    '#F97316',
                                    '#0EA5E9',
                                    '#EC4899',
                                    '#EAB308',
                                    '#6366F1',
                                    '#14B8A6',
                                ]

                                return (
                                    <div style={{ width, height: height, position: 'relative' }}>
                                        <AreaChart
                                            width={width}
                                            height={height - 70}
                                            data={finalData}
                                            margin={{ top: 16, right: 24, left: 8, bottom: 50 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey={xKey}  // IMPORTANT: use xKey, not axis
                                                interval={0}
                                                tick={(props) => renderXAxisTick({ ...props, t })}
                                            />
                                            <YAxis />
                                            {/* keep your Tooltip */}
                                            {keys.map((keyObject, index) => (
                                                <Area
                                                    key={keyObject.key}
                                                    dataKey={keyObject.key}
                                                    stroke={COLORS[index % COLORS.length]}
                                                    fill={COLORS[index % COLORS.length]}
                                                    fillOpacity={0.2}
                                                    stackId="a"
                                                    dot={false}
                                                    isAnimationActive={true}
                                                />
                                            ))}
                                        </AreaChart>

                                        {/* your custom legend uses `keys` local */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                overflowY: 'auto',
                                                columnGap: 16,
                                                rowGap: 6,
                                                marginTop: 10,
                                                marginLeft: 58,
                                            }}
                                        >
                                            {keys.map((k, index) => (
                                                <div
                                                    key={k.key}
                                                    style={{
                                                        flex: '1 1 420px',
                                                        alignItems: 'start',
                                                        display: 'flex',
                                                        gap: 8,
                                                        fontSize: 12,
                                                        marginBottom: 6,
                                                        justifyContent: 'flex-start',
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: 2,
                                                            backgroundColor: COLORS[index % COLORS.length],
                                                            flex: '0 0 auto',
                                                        }}
                                                    />
                                                        <span style={{ textAlign: 'left', lineHeight: 1.2 }}>
                                                          {t ? t(k.label) : k.label}
                                                        </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            }

                            return null
                        }}
                    </AutoSizer>
                </div>
            </div>
        )
    }
}

export default ChartContainer
